import type {
  SamplerRequest,
  SamplerUser,
  SamplerUserParams,
  SamplingJob,
  SamplingJobMetric,
  SamplingJobMetricParams,
  SamplingJobParams,
  SamplingJobRequest,
  SamplingJobSampler,
  SamplingJobSampleType,
  SamplingJobStatusMetric,
  SamplingJobStatusMetricParams,
  SamplingJobStatusRef,
} from './api.types';
import type { UserSession } from '../context/auth-context.types.ts';

const handleResponse = (response: Response) => {
  if (response.status === 401) {
    login();
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
};

const get = async <TResponse>(url: string): Promise<TResponse> => {
  const response = await fetch(url, { credentials: 'include' });

  handleResponse(response);

  return (await response.json()) as TResponse;
};

const put = async <TBody>(url: string, request?: TBody): Promise<Response> => {
  const response = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: request ? JSON.stringify(request) : undefined,
  });

  handleResponse(response);

  return response;
};

export function login(force = false) {
  const urlParams = new URLSearchParams();
  if (force) urlParams.append('prompt', 'login');
  const queryString = urlParams.toString();

  window.location.href = `/api/login?${queryString}`;
}

export async function getSession(options?: { signal?: AbortSignal }): Promise<UserSession> {
  const response = await fetch('/api/session', {
    method: 'GET',
    cache: 'no-store',
    signal: options?.signal,
    headers: {
      'Cache-Control': 'no-cache',
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as UserSession;
}

export async function deleteSession() {
  const response = await fetch('/api/session', { method: 'DELETE' });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
}

export const getSamplingJobs = async (params?: SamplingJobParams): Promise<SamplingJob[]> => {
  const showInactive = params?.showInactive;
  const showUnassigned = params?.showUnassigned;
  const startDate = params?.startDate;
  const endDate = params?.endDate;

  let url = '/api/sampling-jobs';

  const urlParams = new URLSearchParams();

  if (showInactive !== undefined) urlParams.append('showInactive', String(showInactive));
  if (showUnassigned !== undefined) urlParams.append('showUnassigned', String(showUnassigned));
  if (startDate !== undefined) urlParams.append('startDate', startDate.toISOString());
  if (endDate !== undefined) urlParams.append('endDate', endDate.toISOString());

  const queryString = urlParams.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  return await get<SamplingJob[]>(url);
};

export const updateSamplingJob = async (samplingJob: SamplingJobRequest): Promise<Response> => {
  return await put('/api/sampling-jobs', samplingJob);
};

export const getSamplingJobStatuses = async (): Promise<SamplingJobStatusRef[]> => {
  return await get<SamplingJobStatusRef[]>('/api/sampling-jobs/statuses');
};

export const getSamplingJobSampleTypes = async (jobId: number): Promise<SamplingJobSampleType[]> => {
  return await get<SamplingJobSampleType[]>(`/api/sampling-jobs/${jobId}/sample-types`);
};

export const getSamplingJobSamplers = async (jobId: number): Promise<SamplingJobSampler[]> => {
  return await get<SamplingJobSampler[]>(`/api/sampling-jobs/${jobId}/samplers`);
};

export const getSamplers = async (params?: SamplerUserParams): Promise<SamplerUser[]> => {
  const showInactive = params?.showInactive;

  let url = '/api/samplers';

  const urlParams = new URLSearchParams();

  if (showInactive !== undefined) urlParams.append('showInactive', String(showInactive));

  const queryString = urlParams.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  return await get<SamplerUser[]>(url);
};

export const updateSampler = async (sampler: SamplerRequest): Promise<Response> => {
  return await put('/api/samplers', sampler);
};

export const getSamplingJobMetrics = async (params?: SamplingJobMetricParams): Promise<SamplingJobMetric[]> => {
  const startDate = params?.startDate;
  const endDate = params?.endDate;

  let url = '/api/metrics/sampling-jobs';

  const urlParams = new URLSearchParams();

  if (startDate != undefined) urlParams.append('startDate', startDate.toISOString());
  if (endDate != undefined) urlParams.append('endDate', endDate.toISOString());

  const queryString = urlParams.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  return await get<SamplingJobMetric[]>(url);
};

export const getSamplingJobStatusMetrics = async (
  params?: SamplingJobStatusMetricParams
): Promise<SamplingJobStatusMetric[]> => {
  const startDate = params?.startDate;
  const endDate = params?.endDate;

  let url = '/api/metrics/sampling-jobs/statuses';

  const urlParams = new URLSearchParams();

  if (startDate != undefined) urlParams.append('startDate', startDate.toISOString());
  if (endDate != undefined) urlParams.append('endDate', endDate.toISOString());

  const queryString = urlParams.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  return await get<SamplingJobStatusMetric[]>(url);
};
